import { useState } from 'react';
import { AxiosError } from 'axios';
import {
  UserPlus,
  Upload,
  Copy,
  Check,
  X,
  Loader2,
  AlertTriangle,
  Shield,
  User as UserIcon,
  Power,
  Download,
  KeyRound,
} from 'lucide-react';
import { clsx } from 'clsx';

import PageHeader from '@/components/common/PageHeader';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorBanner from '@/components/common/ErrorBanner';
import {
  useUsers,
  useCreateUser,
  useBulkCreateUsers,
  useResetOtp,
  useSetActive,
} from '@/hooks/useUsers';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import type {
  BulkCreateUsersResponse,
  CreateUserResponse,
  RegenerateOtpResponse,
  UserRole,
  UserSummary,
} from '@/types';

/**
 * Admin user-management page.
 *
 * Two ways to onboard:
 *  - "Create user" → modal with a single form
 *  - "Bulk import" → modal with a CSV textarea (paste 90 users at once)
 *
 * After either path, an OTP-display modal pops up. The OTP is shown ONCE.
 * Admin copies it and shares with the new user out-of-band (Slack/WhatsApp).
 * Closing the modal means the OTP is gone forever — they'd have to click
 * "Reset OTP" on the user row to generate a new one.
 */
export default function AdminUsers() {
  // ── Data ─────────────────────────────────────────────────────────────
  const { data: users, isLoading, error, refetch } = useUsers();
  const createUser = useCreateUser();
  const bulkCreate = useBulkCreateUsers();
  const resetOtp = useResetOtp();
  const setActive = useSetActive();
  const { data: authConfig } = useAuthConfig();

  // ── Modal state ──────────────────────────────────────────────────────
  // We use a single discriminated-union for "what modal is open" so we
  // can't accidentally have two modals visible at once.
  const [activeModal, setActiveModal] = useState<
    | { kind: 'none' }
    | { kind: 'create' }
    | { kind: 'bulk' }
    | { kind: 'otp'; response: CreateUserResponse | RegenerateOtpResponse }
    | { kind: 'bulkResults'; response: BulkCreateUsersResponse }
  >({ kind: 'none' });

  if (isLoading) return <LoadingSpinner message="Loading users..." />;
  if (error) {
    return (
      <ErrorBanner
        title="Failed to load users"
        message={extractErrorMessage(error)}
        onRetry={refetch}
      />
    );
  }

  const items = users ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Create and manage local-auth user accounts for the IDP portal"
        actions={
          <>
            <button
              onClick={() => setActiveModal({ kind: 'bulk' })}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
            >
              <Upload className="h-4 w-4" />
              Bulk import
            </button>
            <button
              onClick={() => setActiveModal({ kind: 'create' })}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              Create user
            </button>
          </>
        }
      />

      {!authConfig?.auth_enforced && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <p className="font-medium">Bootstrap mode — login is not yet enforced.</p>
            <p className="mt-0.5 text-amber-800">
              Onboard the team here, then set <code className="font-mono text-xs">LOCAL_AUTH_ENFORCED=true</code>{' '}
              on the API and redeploy to require sign-in.
            </p>
          </div>
        </div>
      )}

      {/* User counts */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Stat label="Total users" value={items.length} />
        <Stat
          label="Admins"
          value={items.filter((u) => u.role === 'admin').length}
          tone="brand"
        />
        <Stat
          label="Active"
          value={items.filter((u) => u.is_active).length}
          tone="emerald"
        />
        <Stat
          label="Need password reset"
          value={items.filter((u) => u.must_change_password).length}
          tone="amber"
        />
      </div>

      {/* Users table */}
      {items.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
          <UserIcon className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-4 text-base font-medium text-slate-700">No users yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Click <span className="font-medium">Create user</span> to add your first admin.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <Th>User</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Auth</Th>
                <Th>Last login</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isResetting={resetOtp.isPending && resetOtp.variables === u.id}
                  isToggling={
                    setActive.isPending && setActive.variables?.userId === u.id
                  }
                  onResetOtp={async () => {
                    try {
                      const resp = await resetOtp.mutateAsync(u.id);
                      setActiveModal({ kind: 'otp', response: resp });
                    } catch {
                      /* mutation surfaces error via toast in future PR */
                    }
                  }}
                  onToggleActive={() =>
                    setActive.mutate({ userId: u.id, active: !u.is_active })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {activeModal.kind === 'create' && (
        <CreateUserModal
          isSubmitting={createUser.isPending}
          onClose={() => setActiveModal({ kind: 'none' })}
          onSubmit={async (payload) => {
            try {
              const resp = await createUser.mutateAsync(payload);
              setActiveModal({ kind: 'otp', response: resp });
            } catch (err) {
              return extractErrorMessage(err);
            }
          }}
        />
      )}

      {activeModal.kind === 'bulk' && (
        <BulkImportModal
          isSubmitting={bulkCreate.isPending}
          onClose={() => setActiveModal({ kind: 'none' })}
          onSubmit={async (csv_text) => {
            try {
              const resp = await bulkCreate.mutateAsync({ csv_text });
              setActiveModal({ kind: 'bulkResults', response: resp });
            } catch (err) {
              return extractErrorMessage(err);
            }
          }}
        />
      )}

      {activeModal.kind === 'otp' && (
        <OtpDisplayModal
          response={activeModal.response}
          onClose={() => setActiveModal({ kind: 'none' })}
        />
      )}

      {activeModal.kind === 'bulkResults' && (
        <BulkResultsModal
          response={activeModal.response}
          onClose={() => setActiveModal({ kind: 'none' })}
        />
      )}
    </div>
  );
}

// ── Small subcomponents ─────────────────────────────────────────────────

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={clsx(
        'px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'brand' | 'emerald' | 'amber';
}) {
  const toneClasses = {
    neutral: 'text-slate-900',
    brand: 'text-brand-700',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className={clsx('mt-1 text-2xl font-bold', toneClasses[tone])}>{value}</p>
    </div>
  );
}

function UserRow({
  user,
  isResetting,
  isToggling,
  onResetOtp,
  onToggleActive,
}: {
  user: UserSummary;
  isResetting: boolean;
  isToggling: boolean;
  onResetOtp: () => void;
  onToggleActive: () => void;
}) {
  const canResetOtp = user.has_password && !user.azure_ad_linked;
  return (
    <tr className={clsx(!user.is_active && 'bg-slate-50/60')}>
      <td className="whitespace-nowrap px-6 py-3">
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              'flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold',
              user.role === 'admin'
                ? 'bg-brand-100 text-brand-700'
                : 'bg-slate-100 text-slate-600',
            )}
          >
            {user.display_name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900">{user.display_name}</p>
            <p className="text-xs text-slate-500 font-mono">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-6 py-3">
        {user.role === 'admin' ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
            <Shield className="h-3 w-3" />
            Admin
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
            <UserIcon className="h-3 w-3" />
            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-6 py-3">
        {!user.is_active ? (
          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
            Disabled
          </span>
        ) : user.must_change_password ? (
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
            Pending first login
          </span>
        ) : (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            Active
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-6 py-3 text-xs text-slate-600">
        {user.azure_ad_linked ? 'Azure AD' : 'Email + password'}
      </td>
      <td className="whitespace-nowrap px-6 py-3 text-xs text-slate-500">
        {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : '—'}
      </td>
      <td className="whitespace-nowrap px-6 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          {canResetOtp && (
            <button
              onClick={onResetOtp}
              disabled={isResetting}
              title="Generate a new OTP — the current password becomes unusable"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {isResetting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <KeyRound className="h-3 w-3" />
              )}
              Reset OTP
            </button>
          )}
          <button
            onClick={onToggleActive}
            disabled={isToggling}
            title={user.is_active ? 'Disable this user' : 'Re-enable this user'}
            className={clsx(
              'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs disabled:opacity-50',
              user.is_active
                ? 'border-red-200 bg-white text-red-700 hover:bg-red-50'
                : 'border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50',
            )}
          >
            {isToggling ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Power className="h-3 w-3" />
            )}
            {user.is_active ? 'Disable' : 'Enable'}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Modals ──────────────────────────────────────────────────────────────

interface ModalShellProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
}

function ModalShell({ title, onClose, children, maxWidth = 'max-w-md' }: ModalShellProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={clsx('bg-white rounded-lg shadow-2xl w-full', maxWidth)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CreateUserModal({
  isSubmitting,
  onClose,
  onSubmit,
}: {
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: { email: string; display_name: string; role: UserRole }) => Promise<string | undefined>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [error, setError] = useState<string | null>(null);

  return (
    <ModalShell title="Create user" onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          const msg = await onSubmit({ email, display_name: name, role });
          if (msg) setError(msg);
        }}
        className="space-y-4 px-6 py-4"
      >
        <div>
          <label className="block text-sm font-medium text-slate-700">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="sri@circuitry.ai"
            className="mt-1 w-full rounded-md border border-slate-300 bg-white py-2 px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            autoFocus
          />
          <p className="mt-1 text-xs text-slate-500">Must end with <span className="font-mono">@circuitry.ai</span></p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Full name</label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Srinivasulu Nuthi"
            className="mt-1 w-full rounded-md border border-slate-300 bg-white py-2 px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="mt-1 w-full rounded-md border border-slate-300 bg-white py-2 px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="user">User — can Start/Stop namespaces</option>
            <option value="admin">Admin — can also manage users</option>
          </select>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Creating…
              </>
            ) : (
              'Create user'
            )}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function BulkImportModal({
  isSubmitting,
  onClose,
  onSubmit,
}: {
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (csv: string) => Promise<string | undefined>;
}) {
  const [csv, setCsv] = useState('email,name,role\n');
  const [error, setError] = useState<string | null>(null);

  return (
    <ModalShell title="Bulk import users" onClose={onClose} maxWidth="max-w-2xl">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          const msg = await onSubmit(csv);
          if (msg) setError(msg);
        }}
        className="space-y-4 px-6 py-4"
      >
        <p className="text-sm text-slate-600">
          Paste a CSV below. First row must be the header{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">email,name,role</code>.
          Role can be <span className="font-mono">admin</span> or <span className="font-mono">user</span> (defaults to{' '}
          <span className="font-mono">user</span> if blank). Failures are reported per row — one
          bad line won't stop the rest.
        </p>
        <textarea
          required
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={12}
          spellCheck={false}
          className="w-full rounded-md border border-slate-300 bg-slate-50 p-3 font-mono text-xs text-slate-900 focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
          placeholder={`email,name,role\nsri@circuitry.ai,Srinivasulu Nuthi,admin\nalice@circuitry.ai,Alice Kumar,user\nbob@circuitry.ai,Bob Singh,user`}
        />

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Importing…
              </>
            ) : (
              'Import users'
            )}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function OtpDisplayModal({
  response,
  onClose,
}: {
  response: CreateUserResponse | RegenerateOtpResponse;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(response.otp);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — older browsers / locked permissions
    }
  };

  return (
    <ModalShell title="One-time password" onClose={onClose}>
      <div className="space-y-4 px-6 py-4">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <p>
              This OTP is shown <strong>only once</strong>. Copy it now and share with{' '}
              <span className="font-mono">{response.user.email}</span> via Slack / WhatsApp.
              They'll use it to log in for the first time, then set their own password.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-slate-500">
            OTP for {response.user.display_name}
          </label>
          <div className="mt-1 flex items-stretch gap-2">
            <code className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-4 py-3 font-mono text-lg tracking-wider text-slate-900 text-center select-all">
              {response.otp}
            </code>
            <button
              onClick={copy}
              className={clsx(
                'flex items-center gap-1.5 rounded-md border px-4 text-sm font-medium transition-colors',
                copied
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
              )}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700"
          >
            Done
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function BulkResultsModal({
  response,
  onClose,
}: {
  response: BulkCreateUsersResponse;
  onClose: () => void;
}) {
  const exportCsv = () => {
    // Generate a downloadable CSV with email + OTP + status for successes.
    // Failures get an "error" column. Admin can open this in Excel and send
    // each user their credentials.
    const header = 'email,name,role,status,otp,error\n';
    const rows = response.rows
      .map((r) =>
        [
          r.email,
          r.name ?? '',
          r.role ?? '',
          r.success ? 'created' : 'failed',
          r.otp ?? '',
          (r.error ?? '').replaceAll(',', ';'),
        ]
          .map((field) => `"${String(field).replaceAll('"', '""')}"`)
          .join(','),
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `idp-bulk-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ModalShell title="Bulk import results" onClose={onClose} maxWidth="max-w-4xl">
      <div className="space-y-4 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-3 text-sm">
            <span className="rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-700">
              {response.succeeded} succeeded
            </span>
            {response.failed > 0 && (
              <span className="rounded-full bg-red-50 px-3 py-1 font-medium text-red-700">
                {response.failed} failed
              </span>
            )}
            <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
              {response.total} total
            </span>
          </div>
          <button
            onClick={exportCsv}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
            title="Download CSV including all OTPs — last chance to save them"
          >
            <Download className="h-4 w-4" />
            Export CSV (with OTPs)
          </button>
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="h-3.5 w-3.5 inline-block mr-1 -mt-0.5" />
          OTPs are shown <strong>only here</strong>. Export the CSV before closing — there's
          no way to retrieve them afterwards.
        </div>

        <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <Th>Email</Th>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Result</Th>
                <Th>OTP / Error</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {response.rows.map((r, i) => (
                <tr key={i} className={r.success ? '' : 'bg-red-50/40'}>
                  <td className="whitespace-nowrap px-6 py-2 font-mono text-xs text-slate-700">
                    {r.email}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2 text-slate-700">{r.name ?? '—'}</td>
                  <td className="whitespace-nowrap px-6 py-2 text-slate-600">{r.role ?? '—'}</td>
                  <td className="whitespace-nowrap px-6 py-2">
                    {r.success ? (
                      <span className="text-xs font-medium text-emerald-700">Created</span>
                    ) : (
                      <span className="text-xs font-medium text-red-700">Failed</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-2">
                    {r.success ? (
                      <code className="rounded bg-slate-100 px-2 py-1 font-mono text-xs">
                        {r.otp}
                      </code>
                    ) : (
                      <span className="text-xs text-red-700">{r.error}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700"
          >
            Done
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ── Utilities ───────────────────────────────────────────────────────────

/**
 * Extract a friendly message from an axios error. FastAPI returns
 * `{detail: "..."}` for HTTPException — we surface that verbatim so the
 * user sees "email already exists" rather than "Request failed 409".
 */
function extractErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
  }
  if (err instanceof Error) return err.message;
  return 'An unexpected error occurred.';
}
